/**
 * 지표 산출 가능성 — 노후화 분석의 목차 (DESIGN §8.11.2).
 *
 * **지표를 새로 만들지 않는다.** KTC → 환경공단 「노후 급속충전기 성능·신뢰성 지표 개발을
 * 위한 데이터 제공 요청서 v1.0」(2026-08-07) §6 의 10개 지표를 축으로 삼고, 각 지표를 **지금
 * 어떤 원천으로 낼 수 있는가**를 먼저 보인다. 말할 수 없는 지표를 말할 수 있는 것처럼 그리면
 * 노후 판정이 교체 예산을 움직이는 순간 피해가 난다.
 *
 * 값이 아니라 **설계 판단**이라 정적이다. 원천이 바뀌면 DESIGN 과 함께 고친다.
 */

type Level = 'ok' | 'proxy' | 'part' | 'no'

const LEVEL_LABEL: Record<Level, string> = {
  ok: '가능',
  proxy: '대리값',
  part: '부분',
  no: '불가',
}

interface Row {
  name: string
  layer: string
  pub: [Level, string]
  keco: [Level, string]
  blocked: string
  approach?: string
}

const ROWS: Row[] = [
  { name: 'MTBF', layer: 'C·D', pub: ['proxy', '상태 DOWN'], keco: ['ok', '지속고장'], blocked: '공개: 고장코드 없음', approach: '④' },
  { name: 'MTTR', layer: 'C', pub: ['no', ''], keco: ['proxy', '알람 해제까지'], blocked: '접수·도착·복구 3시점 없음' },
  { name: 'MDT', layer: 'C', pub: ['proxy', 'DOWN 세션 길이'], keco: ['ok', ''], blocked: '' },
  { name: 'Uptime', layer: 'D', pub: ['proxy', '계획정지 미구분'], keco: ['proxy', ''], blocked: '계획정지 플래그 없음' },
  { name: '충전 성공률', layer: 'B', pub: ['no', ''], keco: ['proxy', '0kWh 세션'], blocked: '종료사유·미개시 시도 없음' },
  { name: '출력 달성률', layer: 'B·A', pub: ['no', ''], keco: ['proxy', '평균 전력 ÷ 정격'], blocked: '세션 최대출력 없음', approach: '⑤' },
  { name: '누적 스트레스', layer: 'B', pub: ['proxy', '충전중 시간'], keco: ['ok', '누적 kWh·세션'], blocked: '', approach: '⑥' },
  { name: '부위별 고장률', layer: 'C', pub: ['no', ''], keco: ['part', '알람 대분류'], blocked: '부위 분류·교체 부품 없음' },
  { name: '계량 정확도', layer: 'E', pub: ['no', ''], keco: ['no', ''], blocked: '재검정 이력 없음' },
  { name: '수명 분포', layer: 'A·F', pub: ['part', '사라짐 이벤트'], keco: ['ok', '철거일 DEL_DT'], blocked: '철거 사유 없음', approach: '②' },
]

function Cell({ v }: { v: [Level, string] }) {
  return (
    <td className={`imx__cell imx__cell--${v[0]}`}>
      <span className="imx__level">{LEVEL_LABEL[v[0]]}</span>
      {v[1] && <span className="imx__how">{v[1]}</span>}
    </td>
  )
}

export function IndicatorMatrix() {
  return (
    <div className="imx">
      <div className="imx__scroll" tabIndex={0} role="region" aria-label="노후 지표 산출 가능성 표">
        <table className="imx__table">
          <caption className="sr-only">
            노후 지표 10종의 원천별 산출 가능성 (KTC 데이터 요청서 v1.0 기준)
          </caption>
          <thead>
            <tr>
              <th scope="col">지표</th>
              <th scope="col">요청서 계층</th>
              <th scope="col">공개 API (전국)</th>
              <th scope="col">환경공단 (ME 직접설치)</th>
              <th scope="col">막힌 이유</th>
              <th scope="col">이 화면</th>
            </tr>
          </thead>
          <tbody>
            {ROWS.map((r) => (
              <tr key={r.name}>
                <th scope="row">{r.name}</th>
                <td className="imx__layer">{r.layer}</td>
                <Cell v={r.pub} />
                <Cell v={r.keco} />
                <td className="imx__blocked">{r.blocked || '—'}</td>
                <td className="imx__approach">{r.approach ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="imx__foot">
        계층 A~F 는 요청서 §3.2 의 데이터 계층(A 설비 마스터 · B 이용 이력 · C 고장·장애 이력 · D
        상태 로그 · E 성능 열화 직접지표 · F 점검·교체·민원)입니다. <strong>대리값</strong>은 정의가 요청서의 지표와 다르다는 뜻입니다 — 예를 들어
        공개 API 의 DOWN 은 고장 사건이 아니라 상태 표시이고, 환경공단의 평균 전력은 최대
        출력이 아닙니다. 전압·전류는 어느 원천에도 없습니다(§8.9.1).
      </p>
    </div>
  )
}
